import React, {useContext, useRef, useState} from 'react';
import {LayoutContext} from "../../../../../../../layout/context/LayoutContext";
import {Toast} from "primereact/toast";
import {usePagination} from "../../../../../../../layout/hooks/usePagination";
import {useFetch} from "../../../../../../../layout/hooks/useFetch";
import {webInfo} from "../../../../../../../utils/webInfo";
import callToast, {handleChangeData, postData, validateFormFields} from "../../../../../../../utils/helper";
import {Button} from "primereact/button";
import PageHeading from "../../../../../../../Components/PageHeading";
import {DataTable, DataTableRowToggleEvent} from "primereact/datatable";
import {Column} from "primereact/column";
import {Dialog} from "primereact/dialog";
import Label from "../../../../../../../Components/Label";
import {InputText} from "primereact/inputtext";
import {InputTextarea} from "primereact/inputtextarea";
import {Dropdown} from "primereact/dropdown";

// Mapbox Imports
import mapboxgl from 'mapbox-gl';
// @ts-ignore
import MapboxGeocoder from '@mapbox/mapbox-gl-geocoder';
import 'mapbox-gl/dist/mapbox-gl.css';
import '@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css';
import {useNavigate} from "react-router-dom";
import {routes} from "../../../../../../../Router/routes";
import {ParkingCampus, ParkingCampusFloor} from "../../../../../../../types/demo";
import CampusFloorDetails from "../Components/CampusFloorDetails";
import {InputNumber} from "primereact/inputnumber";

const VehicleParkingCampus = () => {
    const initialState: ParkingCampus = {
        name: '',
        address: '',
        latitude: null,
        longitude: null,
        status: {id: 1, name: 'Active'},
    };

    const floorInitialState: ParkingCampusFloor = {
        id: Date.now(),
        floor_name: '',
        floor_level: null,
        floor_description: '',
    }

    const {accessToken} = useContext(LayoutContext)
    const [refresh, setRefresh] = useState<boolean>(false);
    const [formData, setFormData] = useState<ParkingCampus>(initialState);
    const [floorFormData, setFloorFormData] = useState<ParkingCampusFloor>(floorInitialState);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [isFloorSubmitting, setIsFloorSubmitting] = useState<boolean>(false);
    const toast = useRef<Toast | null>(null)
    const navigate = useNavigate()

    const [modal, setModal] = useState<boolean>(false);
    const [floorModal, setFloorsModal] = useState<boolean>(false);
    const [campusId, setCampusId] = useState<number | null>(null);

    const [updatedId, setUpdatedId] = useState<number | null>(null);
    const [updatedFloorId, setUpdatedFloorId] = useState<number | null>(null);
    const [mapModal, setMapModal] = useState<boolean>(false);
    const [expandedRows, setExpandedRows] = useState<any | undefined>(undefined);


    const statuses = [
        {id: 1, name: 'Active'},
        {id: 0, name: 'Inactive'},
    ];

    const {page, limitData, handlePageChange} = usePagination();
    const mainTitle = "Parking Campuses"
    const tableDataEndpointUrl = `/vehicle-parking-campuses?page=${page} `
    const submitFormEndpointUrl = updatedId ? `/vehicle-parking-campuses/${updatedId}` : "/vehicle-parking-campuses";
    const submitFloorFormEndpointUrl = updatedFloorId ? `/vehicle-parking-campuses/${updatedFloorId}/floors-store` : `/vehicle-parking-campuses/${campusId}/floors-store`;
    const {data: tableDataList, totalData, loading} = useFetch(tableDataEndpointUrl, [refresh])

    // map refs
    const mapContainerRef = useRef<HTMLDivElement>(null);
    const showMapContainerRef = useRef<HTMLDivElement>(null);
    const mapRef = useRef<mapboxgl.Map | null>(null);
    const markerRef = useRef<mapboxgl.Marker | null>(null);


    const initMap = (mode: 'edit' | 'view') => {
        // Choose the correct container based on the mode
        const targetContainer = mode === 'edit' ? mapContainerRef.current : showMapContainerRef.current;
        if (!targetContainer) return;

        mapboxgl.accessToken = webInfo?.mapBoxToken


        const rawLat = parseFloat(String(formData.latitude));
        const rawLng = parseFloat(String(formData.longitude));
        const isValidLat = !isNaN(rawLat) && rawLat >= -90 && rawLat <= 90;
        const isValidLng = !isNaN(rawLng) && rawLng >= -180 && rawLng <= 180;

        const finalLng = isValidLng ? rawLng : 90.4125;
        const finalLat = isValidLat ? rawLat : 23.8103;

        mapRef.current = new mapboxgl.Map({
            container: targetContainer,
            style: 'mapbox://styles/mapbox/streets-v12',
            center: [finalLng, finalLat],
            zoom: isValidLat && isValidLng ? 15 : 11
        });

        // Initialize Marker
        markerRef.current = new mapboxgl.Marker({
            draggable: mode === 'edit', // Only draggable in edit mode
            color: "#ef4444"
        })
            .setLngLat([finalLng, finalLat])
            .addTo(mapRef.current);

        if (mode === 'edit') {
            // Edit Mode Events: Click and Drag
            mapRef.current.on('click', (e) => {
                const {lng, lat} = e.lngLat;
                setFormData(prev => ({
                    ...prev,
                    longitude: parseFloat(lng.toFixed(6)),
                    latitude: parseFloat(lat.toFixed(6))
                }));
                markerRef.current?.setLngLat([lng, lat]);
            });

            // for drag
            markerRef.current.on('dragend', () => {
                const lngLat = markerRef.current?.getLngLat();
                if (lngLat) {
                    setFormData(prev => ({
                        ...prev,
                        longitude: parseFloat(lngLat.lng.toFixed(6)),
                        latitude: parseFloat(lngLat.lat.toFixed(6))
                    }));
                }
            });

            // Add Geocoder only in Edit Mode
            const geocoder = new MapboxGeocoder({
                accessToken: mapboxgl.accessToken,
                mapboxgl: mapboxgl,
                marker: false,
                countries: 'bd',
                placeholder: 'Search location...'
            });
            mapRef.current.addControl(geocoder, 'top-left');

            geocoder.on('result', (e: any) => {
                const [lng, lat] = e.result.geometry.coordinates;
                setFormData(prev => ({
                    ...prev,
                    longitude: parseFloat(lng.toFixed(6)),
                    latitude: parseFloat(lat.toFixed(6))
                }));
                markerRef.current?.setLngLat([lng, lat]);
            });
        }
    };

    const destroyMap = () => {
        if (mapRef.current) {
            mapRef.current.remove();
            mapRef.current = null;
            markerRef.current = null;
        }
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        const validationRules = [
            {field: 'name', label: 'Name'},
            {field: 'address', label: 'Address'},
            {field: 'status', label: 'Status'}
        ];

        if (!validateFormFields(formData, validationRules, toast)) return;

        const payload = {
            name: formData.name.replace(/\s+/g, ' ').trim(),
            address: formData?.address?.replace(/\s+/g, ' ').trim(),
            latitude: formData?.latitude?.toString(),
            longitude: formData?.longitude?.toString(),
            status: formData?.status?.id
        };

        try {
            setIsSubmitting(true);
            const response = await fetch(`${webInfo?.baseUrl}${submitFormEndpointUrl}`, postData("PUT", accessToken, payload));
            if (response.ok) {
                callToast(toast, true, `${mainTitle} ${updatedId ? 'updated' : 'created'} successfully`);
                setFormData(initialState);
                setModal(false);
                setUpdatedId(null);
                setRefresh(!refresh);
                destroyMap();
            } else {
                const resData = await response.json();
                callToast(toast, false, resData.error);
            }
        } catch (err) {
            console.error('Failed to update', err);
        } finally {
            setIsSubmitting(false);
        }
    }

    const handleFloorFormSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        const validationRules = [
            {field: 'floor_name', label: 'Name'},
            {field: 'floor_level', label: 'Level'},

        ];

        if (!validateFormFields(floorFormData, validationRules, toast)) return;

        const payload = {
            floor_name: floorFormData?.floor_name.replace(/\s+/g, ' ').trim(),
            floor_level: floorFormData?.floor_level,
            floor_description: floorFormData?.floor_description?.replace(/\s+/g, ' ').trim(),
        };

        try {
            setIsFloorSubmitting(true);
            const response = await fetch(`${webInfo?.baseUrl}${submitFloorFormEndpointUrl}`, postData("PUT", accessToken, payload));
            if (response.ok) {
                callToast(toast, true, `${updatedFloorId ? 'updated' : 'created'} successfully`);
                setFloorFormData(floorInitialState);
                setFloorsModal(false);
                setCampusId(null);
                setUpdatedFloorId(null);
                setRefresh(!refresh);
            } else {
                const resData = await response.json();
                callToast(toast, false, resData.error);
            }
        } catch (err) {
            console.error('Failed to update', err);
        } finally {
            setIsFloorSubmitting(false);
        }
    }

    const actionTemplate = (rowData: any) => {
        return (
            <div className={'flex align-items-center justify-content-end gap-2'}>
                {/*edit button*/}
                <Button
                    icon={'pi pi-pencil'}
                    tooltip={'Edit'}
                    className={'border-rounded w-2rem h-2rem'}
                    outlined
                    onClick={() => {
                        const updatedStatus = rowData.status === 1 ? {id: 1, name: 'Active'} : {
                            id: 0,
                            name: 'Inactive'
                        };
                        setUpdatedId(rowData?.id);
                        setFormData({
                            name: rowData?.name,
                            address: rowData?.address,
                            latitude: rowData?.latitude,
                            longitude: rowData?.longitude,
                            status: updatedStatus
                        });
                        setModal(true);
                    }}
                />
                {/*    Add floors button*/}
                <Button
                    icon={'pi pi-plus'}
                    tooltip={'Add Floors'}
                    className={'border-rounded w-2rem h-2rem'}
                    outlined
                    onClick={() => {

                        // setUpdatedId(rowData?.id);
                        setCampusId(rowData?.id);
                        setFloorsModal(true);
                    }}
                />
            </div>
        )
    }

    function message(msg: string, status: string) {
        return <span className={`custom-badge ${status}`}>{msg}</span>;
    }

    const statusDropdownBodyTemplate = (rowData: any) => {
        return <span
            className={'w-full'}>{rowData.status === 1 ? message("Active", "approved") : message("Inactive", "cancelled")}</span>;
    }

    const addressBodyTemplate = (rowData: any) => {
        const hasLatLng = rowData.latitude && rowData.longitude;
        return (
            <span className={'w-full flex align-items-center gap-2 cursor-pointer'} onClick={() => {
                const updatedStatus = rowData.status === 1 ? {id: 1, name: 'Active'} : {id: 0, name: 'Inactive'};
                setFormData({
                    name: rowData?.name,
                    address: rowData?.address,
                    latitude: rowData?.latitude,
                    longitude: rowData?.longitude,
                    status: updatedStatus
                });
                setMapModal(true);
            }}>
                {hasLatLng && <i className="pi pi-map-marker text-red-500" style={{fontSize: '1rem'}}/>}
                {rowData?.address || "No Address"}
            </span>
        );
    }

    const allowExpansion = (rowData: any) => {
        return rowData?.floors_count > 0;
    };


    const rowExpansionTemplate = (data: any) => {
        return <CampusFloorDetails
            campusId={data.id}
            setFloorsModal={setFloorsModal}
            setUpdatedFloorId={setUpdatedFloorId}
            setFloorFormData={setFloorFormData}
        />;
    };

    // const rowExpansionTemplate = (data: any) => {
    //     console.log('data row expansion: ', data);
    //     return (
    //         <div className="p-4  rounded-md shadow-sm">
    //             <h6 className="text-lg font-semibold mb-2 text-gray-700 flex items-center gap-2">
    //                 Description
    //             </h6>
    //             <p className="text-gray-600 text-base">{data.short_description}</p>
    //         </div>
    //     );
    // };

    return (
        <div className={"card"}>
            <Toast ref={toast}/>
            <PageHeading title={mainTitle} isCreate={true}
                         createFunction={() => navigate(routes.settingsConfigureVehicleParkingCampusesAdd)}
                         back={true}/>

            <DataTable
                value={tableDataList}
                dataKey="id"
                paginator={tableDataList?.length > 0}
                lazy
                first={(page - 1) * limitData}
                rows={limitData}
                totalRecords={totalData}
                loading={loading}
                onPage={handlePageChange}

                expandedRows={expandedRows}
                onRowToggle={(e: DataTableRowToggleEvent) => setExpandedRows(e?.data)}
                rowExpansionTemplate={rowExpansionTemplate}
            >
                <Column expander={allowExpansion} style={{width: '3rem'}}/>
                <Column field="name" header="Name" style={{width: '33%'}}/>
                <Column field="address" header="Address" style={{width: '33%'}} body={addressBodyTemplate}/>
                <Column field="status" header="Status" style={{width: '33%'}} body={statusDropdownBodyTemplate}/>
                <Column header={"Action"} body={actionTemplate} headerClassName={"flex justify-content-end"}/>
            </DataTable>

            {/* EDIT/CREATE MODAL */}
            <Dialog
                visible={modal}
                style={{width: "50rem"}}
                header={updatedId ? `Edit ${mainTitle}` : `New ${mainTitle}`}
                onHide={() => {
                    setModal(false);
                    setUpdatedId(null);
                    setFormData(initialState);
                    destroyMap();
                }} onShow={() => initMap('edit')}
            >
                <form onSubmit={handleSubmit}>
                    <div className="grid">
                        <div className="col-12 md:col-6">
                            <Label label={"Name"} required={!updatedId}/>
                            <InputText
                                name={"name"}
                                id={"name"}
                                placeholder={"Parking campus name"}
                                value={formData.name}
                                onChange={(e) => handleChangeData('name', e.target.value, formData, setFormData)}
                                className="w-full"

                            />
                        </div>
                        <div className="col-12 md:col-6">
                            <Label label={"Status"} required={!updatedId}/>
                            <Dropdown
                                options={statuses}
                                id={"status"}
                                name={"status"}
                                optionLabel="name"
                                value={formData?.status}
                                onChange={(e) => handleChangeData('status', e.value, formData, setFormData)}
                                className="w-full"
                            />
                        </div>
                    </div>
                    <div className="grid mt-2">
                        <div className="col-12">
                            <Label label={"Address"} required={!updatedId}/>
                            <InputTextarea
                                value={formData.address}
                                placeholder={"Parking campus address"}
                                id={"address"}
                                name={"address"}
                                className="w-full"
                                onChange={(e) => handleChangeData('address', e.target.value, formData, setFormData)}
                                rows={3}

                            />
                        </div>
                    </div>
                    <div className="grid mt-2">
                        <div className="col-12">
                            {/*<div className="flex justify-content-between mb-2">*/}
                            {/*    <Label label={"Location"}/>*/}
                            {/*<div className="text-sm font-medium surface-200 px-2 py-1 border-round border-1 border-300">*/}
                            {/*    Lat: {formData.latitude || '---'} | Lng: {formData.longitude || '---'}*/}
                            {/*</div>*/}
                            {/*</div>*/}
                            <Label label={"Location"}/>
                            <div ref={mapContainerRef} style={{
                                height: '350px',
                                width: '100%',
                                borderRadius: '8px',
                                border: '1px solid #dee2e6'
                            }}/>
                        </div>
                    </div>
                    <Button
                        type="submit"
                        label={updatedId ? 'Update' : "Create"}
                        className="p-button-primary mt-4"
                        loading={isSubmitting}
                        size={"small"}
                    />
                </form>
            </Dialog>

            <Dialog
                visible={floorModal}
                style={{width: "50rem"}}
                header={`${updatedFloorId ? 'Edit Floor' : 'Add Floor'}`}
                onHide={() => {
                    setFloorsModal(false)
                    setFloorFormData(floorInitialState)
                    setCampusId(null)
                    setUpdatedFloorId(null)
                }}
            >
                <form onSubmit={handleFloorFormSubmit}>
                    <div className="grid ">
                        <div className="col-12 md:col-4">
                            <Label label={"Name"} required={!updatedId}/>
                            <InputText
                                id="name"
                                value={floorFormData.floor_name}
                                size={100}
                                name={"floor_name"}
                                onChange={(e) => handleChangeData('floor_name', e.target.value, floorFormData, setFloorFormData)}
                                placeholder={"Enter name"}
                                className={`w-full`}

                            />
                        </div>
                        <div className="col-12 md:col-4">
                            <Label label={"Level"} required={!updatedId}/>
                            <InputNumber
                                id="name"
                                value={floorFormData.floor_level}
                                name={"floor_level"}
                                onChange={(e) => handleChangeData('floor_level', e.value, floorFormData, setFloorFormData)}
                                placeholder={"Enter floor level"}
                                className={`w-full`}
                            />
                        </div>
                    </div>

                    <div className="grid md:pt-2">
                        <div className="col-12 ">
                            <Label label={"Description"}/>
                            <InputTextarea
                                id="description"
                                value={floorFormData.floor_description}
                                name={"floor_description"}
                                className={`w-full`}
                                onChange={(e) => handleChangeData('floor_description', e.target.value, floorFormData, setFloorFormData)}
                                placeholder={"Enter description"}
                            />
                        </div>
                    </div>
                    <div className="flex gap-2 mt-2">
                        <Button
                            type="submit"
                            label={updatedId ? 'Update' : "Create"}
                            className="p-button-primary"
                            size={'small'}
                            disabled={isFloorSubmitting}
                        />
                    </div>
                </form>

            </Dialog>
            {/* VIEW MAP MODAL */}
            <Dialog visible={mapModal} style={{width: "50rem"}} header={"Location Details"} onHide={() => {
                setMapModal(false);
                setFormData(initialState);
                destroyMap();
            }} onShow={() => initMap('view')}>
                <div className="grid">
                    <div className="col-12">
                        <div className="surface-100 p-3 border-round mb-3">
                            <div className="font-bold text-lg mb-1">{formData.name}</div>
                            <div className="text-600 mb-2"><i className="pi pi-map-marker mr-1"/>{formData.address}</div>
                        </div>
                        <div ref={showMapContainerRef} style={{
                            height: '400px',
                            width: '100%',
                            borderRadius: '8px',
                            border: '1px solid #dee2e6'
                        }}/>
                    </div>
                </div>
            </Dialog>
        </div>
    );
};

export default VehicleParkingCampus;